import network from "./network";
import axios from "axios";
import { host } from "../config";
import { mcpChatUrl, mcpUsageUrl } from "./const";

let bridgeMcpSessionId = null;
const assistantInFlightRequests = new Map();

export const getAssistantInFlightRequest = (conversationId) =>
  assistantInFlightRequests.get(String(conversationId || "")) || null;

const assistantApiHost = String(process.env.REACT_APP_ASSISTANT_API_HOST || "")
  .trim()
  .replace(/\/$/, "");
const assistantMcpEndpoint = String(process.env.REACT_APP_ASSISTANT_MCP_ENDPOINT || "")
  .trim()
  .replace(/\/$/, "");

const getStockHeroApiHost = () => String(host || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
const getBearerToken = (getState) => getState().auth.token;
const useAssistantBridge = () => Boolean(assistantApiHost);
const APP_PLATFORM = "stockhero";
const DEFAULT_ASSISTANT_MODEL = "gpt-5";
const ASSISTANT_SUPPORT_EMAIL = "admin@stockhero.ai";

const getAssistantAuthHeaders = (getState) => ({
  Authorization: `Bearer ${getBearerToken(getState)}`,
});

const isRetryableAssistantSessionError = (error) => {
  const status = Number(error?.response?.status || error?.status || 0);
  const message = String(
    error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.code ||
      error?.message ||
      error ||
      ""
  ).toLowerCase();

  if (message.includes("unknown session")) {
    return true;
  }
  if ([400, 404, 409].includes(status) && message.includes("session")) {
    return /(unknown|not found|missing|expired|invalid|terminated|closed)/.test(message);
  }
  if (status === 401 && message.includes("authorization") && message.includes("token")) {
    return !message.includes("missing authorization bearer");
  }
  return false;
};

const getAssistantErrorMessage = (error) => {
  const status = Number(error?.response?.status || error?.status || 0);
  const serverMessage = String(
    error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.error ||
      error?.message ||
      error ||
      ""
  ).trim();
  const errorCode = String(error?.code || "").trim();
  const lowerMessage = serverMessage.toLowerCase();

  if (status === 401) {
    return "Your StockHero session has expired. Please sign in again, then ask the assistant one more time.";
  }
  if (status === 403) {
    return "The assistant is connected, but your account is not allowed to perform that request.";
  }
  if (status === 404) {
    return `I could not reach the StockHero Assistant service. Please try again later or contact us at ${ASSISTANT_SUPPORT_EMAIL}.`;
  }
  if (status === 429 || error?.code === "daily_token_limit_reached") {
    return serverMessage || "You have reached today's assistant usage limit. Please try again tomorrow.";
  }
  if (status >= 500) {
    return "The StockHero Assistant server is having trouble right now. Please try again in a minute.";
  }
  if (
    errorCode === "ECONNABORTED" ||
    lowerMessage.includes("timeout") ||
    lowerMessage.includes("timed out")
  ) {
    return "The StockHero Assistant took too long to respond. Please try again.";
  }
  if (
    !error?.response &&
    (
      lowerMessage === "network error" ||
      lowerMessage.includes("network error") ||
      lowerMessage.includes("failed to fetch") ||
      lowerMessage.includes("load failed")
    )
  ) {
    return `I could not reach the StockHero Assistant service. Please try again later or contact us at ${ASSISTANT_SUPPORT_EMAIL}.`;
  }

  return serverMessage || "I could not complete that request. Please try again.";
};

const normalizeRequestMessages = (messageOrMessages) => {
  const rawMessages = Array.isArray(messageOrMessages)
    ? messageOrMessages
    : [{ role: "user", content: messageOrMessages }];

  return rawMessages
    .map((item) => ({
      role: item.role,
      content: String(item.content ?? item.text ?? "").trim(),
    }))
    .filter(
      (item) =>
        (item.role === "user" || item.role === "assistant") && item.content
    );
};

const normalizeUsage = (usage = {}) => {
  const usageWindow = usage.day || usage.daily || usage;
  const used =
    usageWindow.used_tokens ??
    usageWindow.tokens_used ??
    usageWindow.used ??
    usageWindow.total_tokens ??
    usageWindow.totalTokens ??
    usageWindow.total ??
    0;
  const limit =
    usageWindow.daily_limit ??
    usageWindow.token_limit ??
    usageWindow.limit ??
    usageWindow.limitTokens ??
    usageWindow.max_tokens ??
    0;

  return {
    used: Number(used) || 0,
    limit: Number(limit) || 0,
    remaining:
      Number(
        usageWindow.remaining ??
          usageWindow.tokens_remaining ??
          usageWindow.remainingTokens ??
          limit - used
      ) || 0,
    resetAt:
      usageWindow.reset_at ||
      usageWindow.resets_at ||
      usageWindow.resetAt ||
      usageWindow.endAt ||
      null,
  };
};

const normalizeChatResponse = (data = {}) => {
  const answer =
    data.answer ??
    data.response ??
    data.message ??
    data.reply ??
    data.data?.answer ??
    data.data?.response ??
    "";

  return {
    answer,
    usage: normalizeUsage(data.usage || data.token_usage || data.data?.usage || {}),
  };
};

const parseAssistantBridgeResponse = (text = "") => {
  const events = String(text)
    .split("\n\n")
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const lines = chunk.split("\n");
      const event = lines
        .find((line) => line.startsWith("event:"))
        ?.replace(/^event:\s*/, "");
      const data = lines
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.replace(/^data:\s*/, ""))
        .join("\n");
      try {
        return { event, data: JSON.parse(data) };
      } catch {
        return { event, data };
      }
    });

  const error = events.find((item) => item.event === "error")?.data;
  if (error) {
    const parsedError = new Error(error.error || error.message || "Assistant request failed");
    parsedError.code = error.code || null;
    parsedError.status = Number(error.status || 0) || null;
    throw parsedError;
  }

  const final = events.filter((item) => item.event === "final").pop()?.data || {};
  if (final.mcpSessionId) {
    bridgeMcpSessionId = final.mcpSessionId;
  }

  return {
    answer: final.output || final.answer || "",
    usage: normalizeUsage(final.meta?.usage || final.usage || {}),
    conversationId: final.conversationId || null,
  };
};

export const sendAssistantMessage = (messageOrMessages, conversationId = null) => (
  dispatch,
  getState
) => {
  const requestKey = String(conversationId || "");
  const existingRequest = requestKey
    ? assistantInFlightRequests.get(requestKey)
    : null;
  if (existingRequest) {
    return existingRequest.promise;
  }

  const request = new Promise((resolve, reject) => {
    const requestMessages = normalizeRequestMessages(messageOrMessages);
    const latestUserMessage =
      [...requestMessages].reverse().find((item) => item.role === "user")
        ?.content || "";

    if (useAssistantBridge()) {
      const postToAssistantBridge = (mcpSessionId) =>
        axios.post(
          `${assistantApiHost}/api/chat`,
          {
            messages: requestMessages,
            model: DEFAULT_ASSISTANT_MODEL,
            appPlatform: APP_PLATFORM,
            apiHost: getStockHeroApiHost(),
            bearerToken: getBearerToken(getState),
            mcpEndpoint: assistantMcpEndpoint || "http://127.0.0.1:8788/mcp",
            mcpSessionId,
            conversationId,
          },
          {
            responseType: "text",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

      postToAssistantBridge(bridgeMcpSessionId)
        .then((res) => resolve(parseAssistantBridgeResponse(res.data)))
        .catch((err) => {
          if (!isRetryableAssistantSessionError(err)) {
            reject(getAssistantErrorMessage(err));
            return;
          }

          bridgeMcpSessionId = null;
          postToAssistantBridge(null)
            .then((res) => resolve(parseAssistantBridgeResponse(res.data)))
            .catch((retryErr) => reject(getAssistantErrorMessage(retryErr)));
        });
      return;
    }

    network
      .post(
        mcpChatUrl,
        {
          message: latestUserMessage,
          conversation_id: conversationId,
          model: "chatgpt",
        },
        dispatch,
        getState
      )
      .then((res) => {
        const data = res.data || {};
        if (!data.code || data.code === 200) {
          resolve({
            ...normalizeChatResponse(data),
            conversationId:
              data.conversation_id || data.conversationId || data.data?.conversation_id || conversationId,
          });
        } else {
          reject(data.message || data.error || "Assistant request failed");
        }
      })
      .catch((err) => reject(getAssistantErrorMessage(err)));
  });

  if (requestKey) {
    const record = {
      promise: request,
      startedAt: Date.now(),
    };
    assistantInFlightRequests.set(requestKey, record);
    request.then(
      () => {
        if (assistantInFlightRequests.get(requestKey) === record) {
          assistantInFlightRequests.delete(requestKey);
        }
      },
      () => {
        if (assistantInFlightRequests.get(requestKey) === record) {
          assistantInFlightRequests.delete(requestKey);
        }
      }
    );
  }

  return request;
};

export const loadAssistantConversations = () => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    if (!useAssistantBridge()) {
      resolve([]);
      return;
    }

    axios
      .get(`${assistantApiHost}/api/conversations`, {
        params: {
          apiHost: getStockHeroApiHost(),
          appPlatform: APP_PLATFORM,
          model: DEFAULT_ASSISTANT_MODEL,
        },
        headers: getAssistantAuthHeaders(getState),
      })
      .then((res) => resolve(res.data?.conversations || []))
      .catch((err) =>
        reject(err?.response?.data?.error || err?.message || err)
      );
  });
};

export const loadAssistantConversation = (conversationId) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    if (!useAssistantBridge() || !conversationId) {
      resolve(null);
      return;
    }

    axios
      .get(
        `${assistantApiHost}/api/conversations/${encodeURIComponent(
          conversationId
        )}`,
        {
          params: {
            apiHost: getStockHeroApiHost(),
            appPlatform: APP_PLATFORM,
          },
          headers: getAssistantAuthHeaders(getState),
        }
      )
      .then((res) => resolve(res.data?.conversation || null))
      .catch((err) =>
        reject(err?.response?.data?.error || err?.message || err)
      );
  });
};

export const saveAssistantConversation = ({
  conversationId,
  title,
  messages,
  memoryNotes = [],
}) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    if (!useAssistantBridge() || !conversationId) {
      resolve(null);
      return;
    }

    axios
      .put(
        `${assistantApiHost}/api/conversations/${encodeURIComponent(
          conversationId
        )}`,
        {
          apiHost: getStockHeroApiHost(),
          appPlatform: APP_PLATFORM,
          model: DEFAULT_ASSISTANT_MODEL,
          title,
          messages: normalizeRequestMessages(messages),
          memoryNotes,
        },
        {
          headers: getAssistantAuthHeaders(getState),
        }
      )
      .then((res) => resolve(res.data?.conversation || null))
      .catch((err) =>
        reject(err?.response?.data?.error || err?.message || err)
      );
  });
};

export const clearAssistantConversation = (conversationId) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    if (!useAssistantBridge() || !conversationId) {
      resolve(null);
      return;
    }

    axios
      .delete(
        `${assistantApiHost}/api/conversations/${encodeURIComponent(
          conversationId
        )}`,
        {
          params: {
            apiHost: getStockHeroApiHost(),
            appPlatform: APP_PLATFORM,
          },
          headers: getAssistantAuthHeaders(getState),
        }
      )
      .then((res) => resolve(res.data || null))
      .catch((err) => {
        const message = err?.response?.data?.error || err?.message || err;
        if (
          Number(err?.response?.status || 0) === 404 &&
          String(message || "").toLowerCase().includes("conversation not found")
        ) {
          resolve({ ok: true, alreadyDeleted: true });
          return;
        }
        reject(message);
      });
  });
};

export const getAssistantUsage = () => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    if (useAssistantBridge()) {
      axios
        .get(`${assistantApiHost}/api/current-user`, {
          params: {
            apiHost: getStockHeroApiHost(),
          },
          headers: {
            Authorization: `Bearer ${getBearerToken(getState)}`,
          },
        })
        .then((res) => resolve(normalizeUsage(res.data?.usage || {})))
        .catch((err) => reject(err?.message || err));
      return;
    }

    network
      .get(mcpUsageUrl, {}, dispatch, getState)
      .then((res) => {
        const data = res.data || {};
        if (!data.code || data.code === 200) {
          resolve(normalizeUsage(data.usage || data.token_usage || data.data || data));
        } else {
          reject(data.message || data.error || "Unable to load usage");
        }
      })
      .catch((err) => reject(err));
  });
};
